import React, { useState, useEffect, useCallback } from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    InputBase,
    Pagination,
    Paper,
    Divider,
    Stack,
    LinearProgress,
    Chip,
} from '@mui/material';
import { Search, Business, ContentCopy, Key, RestartAlt } from '@mui/icons-material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import axios from 'axios';
import { API_BASE_URL_SECURITY } from '../../../../utils/config';
import { toast } from 'react-toastify';
import Modal from '../../../../components/Modal/Modal';
import CompanyAerialCredits from './CompanyAerialCredits';
import CompanyReportEmails from './CompanyReportEmails';

const securityConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('SecurityToken') || ''}` },
});

const CompanyForm = ({ isSidebarOpen }) => {
    const [nombreEmpresa, setNombreEmpresa] = useState('');
    const [direccion, setDireccion] = useState('');
    const [limiteHectareas, setLimiteHectareas] = useState('');
    const [empresas, setEmpresas] = useState([]);
    const [editingCompanyId, setEditingCompanyId] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogSuccess, setDialogSuccess] = useState(false);

    // Búsqueda y paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');

    // Modal de confirmación de eliminación
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [generatedApiKey, setGeneratedApiKey] = useState('');
    const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);

    // ===============================
    // Cargar empresas
    // ===============================
    const fetchEmpresas = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL_SECURITY}empresas`,
                securityConfig()
            );
            const data = Array.isArray(response.data) ? response.data : [];
            if (data.length === 0) {
                toast.info('No se encontraron empresas');
            }
            setEmpresas(data);
        } catch (error) {
            console.error(error);
            toast.error('Error al obtener empresas');
        }
    }, []);

    useEffect(() => {
        fetchEmpresas();
    }, [fetchEmpresas]);

    // ===============================
    // Alta / edición
    // ===============================
    const clearForm = () => {
        setNombreEmpresa('');
        setDireccion('');
        setLimiteHectareas('');
        setEditingCompanyId(null);
    };

    const handleSubmit = async () => {
        if (!nombreEmpresa || !direccion || !limiteHectareas) {
            toast.error('Todos los campos son obligatorios');
            return;
        }

        const nuevaEmpresa = {
            nombre: nombreEmpresa,
            direccion,
            limiteHectareas,
        };

        try {
            if (editingCompanyId) {
                await axios.put(
                    `${API_BASE_URL_SECURITY}empresas/${editingCompanyId}`,
                    nuevaEmpresa,
                    securityConfig()
                );
                setDialogMessage('Empresa actualizada exitosamente');
            } else {
                await axios.post(
                    `${API_BASE_URL_SECURITY}empresas`,
                    nuevaEmpresa,
                    securityConfig()
                );
                setDialogMessage('Empresa creada exitosamente');
            }

            setDialogSuccess(true);
            setDialogOpen(true);
            await fetchEmpresas();
            clearForm();
        } catch (error) {
            console.error(error);
            setDialogMessage(error.response?.data?.message || 'Error al procesar la empresa.');
            setDialogSuccess(false);
            setDialogOpen(true);
        }
    };

    const handleEditClick = (empresa) => {
        setEditingCompanyId(empresa._id);
        setNombreEmpresa(empresa.nombre || '');
        setDireccion(empresa.direccion || '');
        setLimiteHectareas(empresa.limiteHectareas || '');
    };

    const selectedCompany = empresas.find((empresa) => empresa._id === editingCompanyId);
    const selectedQuota = selectedCompany?.cuota;

    const generateApiKey = async () => {
        if (!editingCompanyId) return;
        try {
            const response = await axios.post(
                `${API_BASE_URL_SECURITY}empresas/${editingCompanyId}/api-keys`,
                { name: 'Integración API' },
                securityConfig()
            );
            setGeneratedApiKey(response.data.apiKey);
            setApiKeyDialogOpen(true);
            await fetchEmpresas();
        } catch (error) {
            toast.error(error.response?.data?.message || 'No fue posible generar el token.');
        }
    };

    const revokeApiKey = async (keyId) => {
        if (!editingCompanyId) return;
        try {
            await axios.delete(
                `${API_BASE_URL_SECURITY}empresas/${editingCompanyId}/api-keys/${keyId}`,
                securityConfig()
            );
            toast.success('Token revocado.');
            await fetchEmpresas();
        } catch (error) {
            toast.error(error.response?.data?.message || 'No fue posible revocar el token.');
        }
    };

    const resetQuota = async () => {
        if (!editingCompanyId) return;
        if (!window.confirm('¿Reiniciar a cero el consumo de hectáreas de esta empresa?')) return;
        try {
            await axios.post(
                `${API_BASE_URL_SECURITY}empresas/${editingCompanyId}/quota/reset`,
                {},
                securityConfig()
            );
            toast.success('Consumo de hectáreas reiniciado.');
            await fetchEmpresas();
        } catch (error) {
            toast.error(error.response?.data?.message || 'No fue posible reiniciar la cuota.');
        }
    };

    // ===============================
    // Eliminación con Modal
    // ===============================
    const handleOpenDeleteModal = () => {
        if (!editingCompanyId) {
            toast.error('Seleccione una empresa para eliminar');
            return;
        }
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const confirmDeleteCompany = async () => {
        if (!editingCompanyId) {
            setIsDeleteModalOpen(false);
            return;
        }

        try {
            await axios.delete(
                `${API_BASE_URL_SECURITY}empresas/${editingCompanyId}`,
                securityConfig()
            );
            setDialogMessage('Empresa y usuarios asociados eliminados con éxito');
            setDialogSuccess(true);
            setDialogOpen(true);

            setEmpresas((prev) => prev.filter((e) => e._id !== editingCompanyId));
            clearForm();
        } catch (error) {
            console.error(error);
            setDialogMessage('Error al eliminar la empresa.');
            setDialogSuccess(false);
            setDialogOpen(true);
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    // ===============================
    // Búsqueda y paginación
    // ===============================
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    const filteredEmpresas = empresas.filter((empresa) =>
        `${empresa.nombre || ''} ${empresa.direccion || ''}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEmpresas = filteredEmpresas.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 3,
                alignItems: 'flex-start',
                padding: 3,
                marginTop: '25px',
                marginLeft: isSidebarOpen ? '250px' : '160px',
                transition: 'margin-left 0.3s ease-in-out',
                backgroundColor: '#f3f4f6',
                minHeight: 'calc(100vh - 25px)',
            }}
        >
            {/* Panel principal: formulario */}
            <Paper
                elevation={3}
                sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '999px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(37, 99, 235, 0.08)',
                        }}
                    >
                        <Business sx={{ color: '#1d4ed8' }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            {editingCompanyId ? 'Editar empresa' : 'Nueva empresa'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Administre los datos maestros de las empresas del sistema.
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <TextField
                    fullWidth
                    label="Nombre de la empresa"
                    margin="normal"
                    variant="outlined"
                    value={nombreEmpresa}
                    onChange={(e) => setNombreEmpresa(e.target.value)}
                    required
                />

                {editingCompanyId && selectedQuota && (
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            Consumo de análisis por API
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={Number(selectedQuota.porcentajeUtilizado || 0)}
                            color={selectedQuota.porcentajeUtilizado >= 90 ? 'error' : 'primary'}
                            sx={{ height: 9, borderRadius: 6, mb: 1.5 }}
                        />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip label={`Límite: ${Number(selectedQuota.limiteHectareas || 0).toFixed(2)} ha`} />
                            <Chip color="primary" label={`Consumidas: ${Number(selectedQuota.hectareasConsumidas || 0).toFixed(2)} ha`} />
                            <Chip color="warning" label={`En proceso: ${Number(selectedQuota.hectareasEnProceso || 0).toFixed(2)} ha`} />
                            <Chip color="success" label={`Disponibles: ${Number(selectedQuota.hectareasDisponibles || 0).toFixed(2)} ha`} />
                        </Stack>
                    </Box>
                )}
                <TextField
                    fullWidth
                    label="Dirección"
                    margin="normal"
                    variant="outlined"
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value)}
                    required
                />
                <TextField
                    fullWidth
                    label="Límite de hectáreas"
                    margin="normal"
                    variant="outlined"
                    type="number"
                    value={limiteHectareas}
                    onChange={(e) => setLimiteHectareas(e.target.value)}
                    required
                />

                {editingCompanyId && selectedCompany && (
                    <CompanyAerialCredits
                        empresa={selectedCompany}
                        securityConfig={securityConfig}
                        onSaved={fetchEmpresas}
                    />
                )}

                {editingCompanyId && selectedCompany && (
                    <CompanyReportEmails
                        empresa={selectedCompany}
                        securityConfig={securityConfig}
                        onSaved={fetchEmpresas}
                    />
                )}

                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: '#111827',
                            color: '#fff',
                            py: 1.2,
                            '&:hover': { backgroundColor: '#000' },
                        }}
                        onClick={handleSubmit}
                    >
                        {editingCompanyId ? 'Guardar cambios' : 'Crear empresa'}
                    </Button>

                    {editingCompanyId && (
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1.5}>
                                <Button variant="outlined" fullWidth startIcon={<Key />} onClick={generateApiKey}>
                                    Generar token API
                                </Button>
                                <Button variant="outlined" fullWidth startIcon={<RestartAlt />} onClick={resetQuota}>
                                    Reiniciar consumo
                                </Button>
                            </Stack>
                            {(selectedCompany?.apiKeys || []).length > 0 && (
                                <Stack spacing={1}>
                                    {(selectedCompany.apiKeys || []).map((apiKey) => (
                                        <Box key={apiKey.keyId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{apiKey.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {apiKey.prefix}… · {apiKey.active ? 'Activo' : 'Revocado'}
                                                </Typography>
                                            </Box>
                                            {apiKey.active && (
                                                <Button size="small" color="error" onClick={() => revokeApiKey(apiKey.keyId)}>
                                                    Revocar
                                                </Button>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                            <Stack direction="row" spacing={1.5}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={clearForm}
                            >
                                Cancelar edición
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                fullWidth
                                onClick={handleOpenDeleteModal}
                            >
                                Eliminar empresa
                            </Button>
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Paper>

            {/* Panel lateral: listado */}
            <Paper
                elevation={2}
                sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        Empresas registradas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Seleccione una empresa para editar su información.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 1,
                        mb: 1,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        border: '1px solid #d1d5db',
                        backgroundColor: '#f9fafb',
                    }}
                >
                    <Search sx={{ mr: 1, fontSize: 20, color: '#6b7280' }} />
                    <InputBase
                        placeholder="Buscar por nombre o dirección…"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        sx={{ flex: 1, fontSize: 14 }}
                    />
                </Box>

                <List
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        maxHeight: 320,
                        overflowY: 'auto',
                        borderRadius: 2,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#ffffff',
                    }}
                >
                    {currentEmpresas.map((empresa) => (
                        <ListItem key={empresa._id} disablePadding>
                            <ListItemButton onClick={() => handleEditClick(empresa)}>
                                <ListItemText
                                    primary={empresa.nombre}
                                    secondary={`${empresa.direccion} · ${Number(empresa.cuota?.hectareasDisponibles ?? empresa.limiteHectareas ?? 0).toFixed(2)} ha disponibles`}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {currentEmpresas.length === 0 && (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                No hay empresas que coincidan con la búsqueda.
                            </Typography>
                        </Box>
                    )}
                </List>

                <Pagination
                    count={Math.ceil(filteredEmpresas.length / itemsPerPage) || 1}
                    page={currentPage}
                    onChange={handlePageChange}
                    size="small"
                    sx={{ alignSelf: 'center', mt: 1 }}
                />
            </Paper>

            {/* Modal de confirmación de eliminación */}
            <Modal isOpen={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '999px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(248, 113, 113, 0.1)',
                            }}
                        >
                            <WarningAmberRoundedIcon sx={{ color: '#dc2626' }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={600}>
                                Eliminar empresa
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Se eliminará la empresa{' '}
                                <b>{nombreEmpresa || 'seleccionada'}</b> y todos los
                                usuarios asociados. Esta acción no se puede deshacer.
                            </Typography>
                        </Box>
                    </Stack>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
                        <Button variant="outlined" onClick={handleCloseDeleteModal}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={confirmDeleteCompany}
                        >
                            Eliminar definitivamente
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Dialog de feedback (éxito / error) */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>{dialogSuccess ? 'Éxito' : 'Error'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">{dialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={apiKeyDialogOpen} onClose={() => setApiKeyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Token API generado</DialogTitle>
                <DialogContent>
                    <Typography color="error" sx={{ mb: 2 }}>
                        Copie este token ahora. Por seguridad no podrá consultarse nuevamente.
                    </Typography>
                    <TextField fullWidth value={generatedApiKey} InputProps={{ readOnly: true }} />
                </DialogContent>
                <DialogActions>
                    <Button
                        startIcon={<ContentCopy />}
                        onClick={() => {
                            navigator.clipboard.writeText(generatedApiKey);
                            toast.success('Token copiado.');
                        }}
                    >
                        Copiar
                    </Button>
                    <Button onClick={() => setApiKeyDialogOpen(false)} variant="contained">Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CompanyForm;
